import { Dropdown } from 'Common/Components/Dropdown';
import TableContainer from 'Common/TableContainer';
import { CheckCircle, CircleEllipsis, FileEdit, MoreHorizontal, PencilLine, Search, Trash2, View } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom';


interface CampaignTableProps {
  campaigns: any[];
  searchText: string;
  campaignId: any;
  onView: (user: any) => void;
  onEdit: (user: any) => void;
  onDelete: (user: any) => void;
  onDuplicate: (user: any) => void;
}

const CampaignTable: React.FC<CampaignTableProps> = ({ campaigns, searchText, campaignId, onView, onEdit, onDelete, onDuplicate }) => {
  const [role, setRole] = useState('');

  useEffect(() => {
    const role = localStorage.getItem('role') || '';
    setRole(role);
  }, [])

  const columns = useMemo(() => [
    {
      header: "Campaigns",
      accessorKey: "name",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div className="flex items-center gap-2" title={cell.getValue()}>
          <div className="grow truncate">
            <h6 className="mb-1">
              <Link to="#!" className="name truncate block max-w-[300px]" title={cell.getValue()}>{cell.getValue()?.charAt(0).toUpperCase() + cell.getValue()?.slice(1)}</Link>
            </h6>
            <p className="text-slate-500 dark:text-zink-200 truncate block max-w-[300px]" title={cell.row.original.designation}>{cell.row.original.designation}</p>
          </div>
        </div>
      ),
    },
    {
      header: "Subject",
      accessorKey: "subject",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div title={cell.getValue()} className="flex items-center gap-2">
          <div className="grow truncate">
            <h6 className="mb-1 truncate block max-w-[500px]">
              {cell?.getValue()}
            </h6>
          </div>
        </div>
      ),
    },
    {
      header: "Type",
      accessorKey: "campaignType",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div title={cell.getValue()} className="flex items-center gap-2">
          <div className="grow truncate">
            <h6 className="mb-1 truncate block max-w-[300px]">
              {cell?.getValue() && cell?.getValue() !== '0' ? cell?.getValue() : '--'}
            </h6>
          </div>
        </div>
      ),
    },
    {
      header: "Date",
      accessorKey: "lastModificationTime",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const lastModificationTime = cell?.getValue();
        const creationTime = cell?.row?.original?.creationTime;
        const displayTime = lastModificationTime || creationTime;
        return (
          <div className="flex items-center gap-2">
            <div className="grow">
              <h6 className="mb-1">
                {formatDateString(displayTime)}
              </h6>
            </div>
          </div>
        );
      },
    },
    {
      header: "Status",
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (cell: any) => (
        cell.getValue() === "Sent" ? (
          <span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status">
            <CheckCircle className="size-3 mr-1.5" />
            Sent
          </span>
        ) : campaignId !== cell?.row?.original?.id ? (
          <span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-red-100 border-transparent text-red-600 dark:bg-slate-500/20 dark:text-zink-200 dark:border-transparent status">
            <PencilLine className="size-3 mr-1.5" />
            Draft
          </span>
        ) : (
          <span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-yellow-100  border-transparent text-yellow-600 dark:bg-slate-500/20 dark:text-zink-200 dark:border-transparent status">
            <CircleEllipsis className="size-3 mr-1.5" />
            Sending
          </span>
        )
      ),
    },
    {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (cell: any) => (
        <td className="!overflow-visible !max-w-none !truncate-none">
          <Dropdown className="relative">
            <Dropdown.Trigger
              className="flex items-center justify-center size-[30px] p-0 text-slate-500 btn bg-slate-100 hover:text-white hover:bg-slate-600 focus:text-white focus:bg-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:ring active:ring-slate-100 dark:bg-slate-500/20 dark:text-slate-400 dark:hover:bg-slate-500 dark:hover:text-white dark:focus:bg-slate-500 dark:focus:text-white dark:active:bg-slate-500 dark:active:text-white dark:ring-slate-400/20"
              id="usersAction1"
            >
              <MoreHorizontal className="size-3" />
            </Dropdown.Trigger>
            <Dropdown.Content
              placement={cell.row.index === campaigns.length - 1 ? "bottom-start" : "right-end"}
              className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right top-[-150px] right-[100px] list-none bg-white rounded-md shadow-md min-w-[10rem] dark:bg-zink-600"
              aria-labelledby="usersAction1"
            >
              {(role === 'admin') ?
                <li>
                  <Link
                    className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                    to="#!"
                    onClick={() => onView(cell.row.original)}
                  >
                    <View className="inline-block size-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">View</span>
                  </Link>
                </li>
                : (<></>)}

              {(role === 'admin' && cell?.row?.original?.status === "Sent") &&
                <li>
                  <Link
                    className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                    to="#!"
                    onClick={() => onDuplicate(cell.row.original)}
                  >
                    <View className="inline-block size-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Duplicate</span>
                  </Link>
                </li>}

              {(cell?.row?.original?.status !== "Sent" && campaignId !== cell?.row?.original?.id) && (
                <li>
                  <Link
                    className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                    to="#!"
                    onClick={() => onEdit(cell.row.original)}
                  >
                    <FileEdit className="inline-block size-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Edit</span>
                  </Link>
                </li>
              )}

              {cell?.row?.original?.status !== "Sent" && (
                <li>
                  <Link
                    className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200"
                    to="#!"
                    onClick={() => onDelete(cell.row.original)}
                  >
                    <Trash2 className="inline-block size-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Delete</span>
                  </Link>
                </li>
              )}
            </Dropdown.Content>
          </Dropdown>
        </td>
      )
    },
  ], [onEdit, onDelete, onDuplicate, role]);

  const formatDateString = (dateString: string): string => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
  };

  return (
    <div className="!pt-2">
      {campaigns.length > 0 ? (
        <TableContainer
          isPagination={true}
          columns={columns}
          data={campaigns}
          customPageSize={10}
          divclassName="sm:h-[calc(100vh-300px)] h-[calc(100vh-360px)] xl:h-[calc(100vh-360px)] 2xl:h-[calc(100vh-300px)] min-h-[200px] max-h-[calc(100vh-248px)] overflow-auto"
          tableclassName="w-full border-separate table-custom border-spacing-y-1 whitespace-nowrap"
          theadclassName="text-left relative rounded-md bg-slate-200 dark:bg-zink-600 after:absolute ltr:after:border-l-2 rtl:after:border-r-2 ltr:after:left-0 rtl:after:right-0 after:top-0 after:bottom-0 after:border-transparent [&.active]:after:border-custom-500 [&.active]:bg-slate-100 dark:[&.active]:bg-zink-600"
          thclassName="px-4 py-2.5 first:pl-5 last:pr-5 font-semibold sticky top-0 bg-gray-100 z-10"
          tdclassName="cursor-pointer px-4 py-2.5 first:pl-5 last:pr-5"
          PaginationClassName="flex flex-col items-center 2xl:mt-8 xl:mt-8 mt-0 sm:mt-0 md:mt-0 md:flex-row"
          tbodyclassName=""
          trclassName="hover:bg-gray-100"
        />
      ) : (
        <div className="noresult">
          <div className="py-6 text-center">
            <Search className="size-6 mx-auto text-slate-500 fill-sky-100 dark:sky-500/20" />
            {searchText !== ""
              ? (<h6 className="mt-2 mb-1">No campaign found</h6>)
              : (<h6 className="mt-2 mb-1">No campaigns created yet. Click 'Create Campaign' to start</h6>)
            }
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignTable